import { Backdrop, CircularProgress, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import {
  cardsStatusSelector,
  deepDiveStatusSelector,
  gantChartStatusSelector,
  lastRunStatusSelector,
  refreshStatusSelector,
} from "../features/Monitor/MonitorSlice";
import { useAppSelector } from "../util/hooks";
import { API_STATUS } from "../util/StaticData";
import {
  marketStatusSelector,
  useCaseStatusSelector,
} from "../features/Monitor/FilterSlice";
import { rawDataQualityStatusSelector } from "../features/DataMetadata/DataMetaDataSlice";

const loadingMessages = [
  "Scanning datasets",
  "Analyzing structure",
  "Matching columns",
  "Interpreting patterns",
  "Validating Relationships",
  "Assessing mapping correctness",
  "Finalizing results",
];

export default function CustomLoader() {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const gantChartStatus = useAppSelector(gantChartStatusSelector);
  const lastRunStatus = useAppSelector(lastRunStatusSelector);
  const cardsStatus = useAppSelector(cardsStatusSelector);
  const useCaseStatus = useAppSelector(useCaseStatusSelector);
  const marketStatus = useAppSelector(marketStatusSelector);
  const refreshPipelineStatus = useAppSelector(refreshStatusSelector);
  const deepDiveDataStatus = useAppSelector(deepDiveStatusSelector);
  const rawDataQualityStatus = useAppSelector(rawDataQualityStatusSelector);
  const rawDataInfoTableStatus = useAppSelector(
    (store) => store.dataMetaData.rawDataInfoTableStatus,
  );
  const maintenanceListStatus = useAppSelector(
    (store) => store.maintenance.maintenanceListStatus,
  );

  const chartStatus = useAppSelector((store) => store.maintenance.chartStatus);

  const executionHistoryStatus = useAppSelector(
    (store) => store.maintenance.executionHistoryStatus,
  );
  const pipelineConfigStatus = useAppSelector(
    (store) => store.maintenance.pipelineConfig.status,
  );
  const schedulePipelineStatus = useAppSelector(
    (store) => store.maintenance.schedulePipelineStatus,
  );

  const configBaseDataStatus = useAppSelector(
    (store) => store.maintenance.configBaseDataStatus,
  );
  const tableDataStatus = useAppSelector(
    (store) => store.modelMetaData.tableDataStatus,
  );
  const chartDataStatus = useAppSelector(
    (store) => store.modelMetaData.chartDataStatus,
  );
  const cardsDataStatus = useAppSelector(
    (store) => store.modelMetaData.cardsDataStatus,
  );
  const featureDriftStatus = useAppSelector(
    (store) => store.dataMetaData.featureDriftStatus,
  );
  const dataQualityStatus = useAppSelector(
    (store) => store.dataQuality.rawData.rawDataStatus,
  );
  const implementedRulesStatus = useAppSelector(
    (store) => store.dataQuality.dataQualityRules.ImplementedRulesStatus,
  );
  const runQCCheck = useAppSelector((store) => store.dataQuality.runQCCheck);
  const rulePipelineStatusLoading = useAppSelector(
    (store) => store.dataQuality.rulePipelineStatusLoading,
  );
  const {
    scenarioDeleteLoading: isScenarioDeleteLoading,
    isDatasetTableLoading,
    scenarioMappingStatus,
  } = useAppSelector((store) => store.harmonization);

  // Use a single boolean to determine if the backdrop should be open
  const openBackdrop =
    gantChartStatus === API_STATUS.LOADING ||
    lastRunStatus === API_STATUS.LOADING ||
    cardsStatus === API_STATUS.LOADING ||
    useCaseStatus === API_STATUS.LOADING ||
    marketStatus === API_STATUS.LOADING ||
    refreshPipelineStatus === API_STATUS.LOADING ||
    deepDiveDataStatus === API_STATUS.LOADING ||
    rawDataQualityStatus === API_STATUS.LOADING ||
    rawDataInfoTableStatus === API_STATUS.LOADING ||
    maintenanceListStatus === API_STATUS.LOADING ||
    chartStatus === API_STATUS.LOADING ||
    executionHistoryStatus === API_STATUS.LOADING ||
    pipelineConfigStatus === API_STATUS.LOADING ||
    schedulePipelineStatus === API_STATUS.LOADING ||
    configBaseDataStatus === API_STATUS.LOADING ||
    tableDataStatus === API_STATUS.LOADING ||
    chartDataStatus === API_STATUS.LOADING ||
    cardsDataStatus === API_STATUS.LOADING ||
    featureDriftStatus === API_STATUS.LOADING ||
    dataQualityStatus === API_STATUS.LOADING ||
    implementedRulesStatus === API_STATUS.LOADING ||
    runQCCheck === API_STATUS.LOADING ||
    rulePipelineStatusLoading === API_STATUS.LOADING ||
    isScenarioDeleteLoading === API_STATUS.LOADING ||
    isDatasetTableLoading === API_STATUS.LOADING ||
    scenarioMappingStatus === API_STATUS.LOADING;

  useEffect(() => {
    let intervalId:any;

    if (scenarioMappingStatus === API_STATUS.LOADING) {
      intervalId = setInterval(() => {
        setCurrentMessageIndex(
          (prevIndex) => (prevIndex + 1) % loadingMessages.length,
        );
      }, 2000); 
    } else {
      setCurrentMessageIndex(0);
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId);
  }, [scenarioMappingStatus]); 

  return (
    <Backdrop
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
      open={openBackdrop}
    >
      <CircularProgress />

      {scenarioMappingStatus === API_STATUS.LOADING ? (
        <Typography variant="body2">
          {loadingMessages[currentMessageIndex]}
        </Typography>
      ) : (
        <Typography variant="body2">Please Wait</Typography>
      )}
    </Backdrop>
  );
}